import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// Mirrors the redacted shape returned by the dashboard API
// (routes/woocommerce-config.ts) — secrets are never sent to the client.
export interface WooCommerceStoreConfig {
  locationId: string;
  tenantId: string;
  storeUrl: string;
  isActive: boolean;
  syncMode: 'interval' | 'daily' | 'scheduled';
  syncIntervalSec: number;
  syncDailyTimePst: string | null;
  syncScheduleTimes: string | null;
  hasCredentials: boolean;
  hasWebhookSecret: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WooCommerceConfigInput {
  storeUrl?: string;
  consumerKey?: string;
  consumerSecret?: string;
  webhookSecret?: string | null;
  isActive?: boolean;
  syncMode?: 'interval' | 'daily' | 'scheduled';
  syncIntervalSec?: number;
  syncDailyTimePst?: string | null;
  syncScheduleTimes?: string[] | null;
}

export function useWooCommerceConfig(locationId?: string | null) {
  return useQuery<{ data: WooCommerceStoreConfig | null }>({
    queryKey: ['woocommerce-config', locationId ?? ''],
    queryFn: () => api.get(`/locations/${locationId}/woocommerce-config`),
    enabled: !!locationId,
  });
}

export function useUpdateWooCommerceConfig(locationId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: WooCommerceConfigInput) => api.put(`/locations/${locationId}/woocommerce-config`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['woocommerce-config', locationId ?? ''] });
    },
  });
}

export function useTestWooCommerceConnection(locationId?: string | null) {
  return useMutation<
    { data: { success: boolean; message: string } },
    unknown,
    { storeUrl?: string; consumerKey?: string; consumerSecret?: string }
  >({
    mutationFn: (creds) => api.post(`/locations/${locationId}/woocommerce-config/test`, creds),
  });
}
